import * as ReactDOM from "react-dom";
import React, { useEffect, useState } from "react";
import { Position, Swipe } from "react-swipe-component";
import { RouteComponentProps, withRouter } from "react-router";
import { useTranslation } from "react-i18next";
import { animated, useTransition } from "react-spring";
import { i18nUserContext } from "config/translations/I18n";
import { Icon } from "@cc/react-components";
import IconType from "@cc/react-components/IconType";

export interface Props
  extends React.HTMLAttributes<HTMLElement>,
    RouteComponentProps {
  /** Callback method that will be executed when the swipe up is ended(the user releases the swipe gesture) */
  onSwipeEnd?: () => void;
  /** Message key of the message being shown during swiping*/
  swipeMessageKey?: string;
  /** Message key of the message that will be shown if the max swipe height is reached */
  swipeConfirmKey?: string;
  /** If set to false the swipe will be deactivated */
  enableSwipe?: boolean;
  /** IconType that will be shown above the confirm message */
  iconType?: IconType;
  /** Ratio of the swipe gesture to mimic swiping up the whole screen to only swipe up a small view */
  swipeDelta?: number;
  /** Maximum height of the swipe up view (in rem)*/
  swipeHeight?: number;
}

const SwipeUp = ({
  children,
  history,
  onSwipeEnd,
  swipeConfirmKey,
  swipeMessageKey,
  iconType,
  enableSwipe = true,
  swipeDelta = 300,
  swipeHeight = 13,
  ...props
}: Props) => {
  const portalRoot = document.getElementById("absolutePortal");
  const [cancelStyle, setCancelStyle] = useState<React.CSSProperties>({
    opacity: 0,
    top: "calc(var(--vh, 1vh) * 100)",
    position: "absolute",
    width: "100%",
    height: swipeHeight + "rem",
    borderTop: "1px solid rgba(206, 198, 206, 0.67)",
    boxShadow:
      "0px 1px 3px 0px rgba(0, 0, 0, 0.2)," +
      "0px 1px 1px 0px rgba(0, 0, 0, 0.14), " +
      "0px 2px 1px -1px rgba(0, 0, 0, 0.12)",
    backgroundColor: "rgba(245,244,246,1)",
  });

  const onSwipeListener = (p: Position) => {
    if (!enableSwipe) {
      return;
    }
    if (p.y !== 0) {
      const swipePercentage = Math.max(0, Math.min((-1 * p.y) / swipeDelta, 1));

      setCancelStyle(
        Object.assign({}, cancelStyle, {
          opacity: swipePercentage,
          top:
            "calc((var(--vh, 1vh) * 100) - " +
            swipeHeight * swipePercentage +
            "rem)",
        })
      );
    }
  };

  const onSwipeDone = () => {
    if (cancelStyle && cancelStyle.opacity && cancelStyle.opacity === 1) {
      onSwipeEnd != null && onSwipeEnd();
    }
    setCancelStyle(
      Object.assign({}, cancelStyle, {
        opacity: 0,
        top: "calc(var(--vh, 1vh) * 100)",
      })
    );
  };

  return (
    <React.Fragment>
      <Swipe
        onSwipe={onSwipeListener}
        onSwipeEnd={onSwipeDone}
        style={{ height: "100%", width: "100%" }}
        detectTouch
        delta={swipeDelta}
      >
        {children}
      </Swipe>
      {portalRoot &&
        ReactDOM.createPortal(
          <RefreshContainer
            style={cancelStyle}
            swipeConfirmKey={swipeConfirmKey}
            swipeMessageKey={swipeMessageKey}
            iconType={iconType}
          />,
          portalRoot
        )}
    </React.Fragment>
  );
};

const RefreshContainer = ({
  style,
  swipeConfirmKey,
  swipeMessageKey,
  iconType,
}: BottomPageDivProps) => {
  const [t] = useTranslation("", { i18n: i18nUserContext });
  const [toggle, setToggle] = useState(false);
  const transitions = useTransition(toggle, null, {
    from: { position: "absolute", opacity: 0, width: "100%" },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  useEffect(() => {
    if (style && style.opacity === 1) {
      setToggle(true);
    } else {
      setToggle(false);
    }
  }, [style]);

  const renderInfo = (iconType: IconType, key: string) => (
    <div className="d-flex w-100 flex-column align-items-center mt-4">
      <Icon type={iconType} />
      <h4>{t(key)}</h4>
    </div>
  );

  return (
    <div style={style}>
      <div style={{ position: "relative" }}>
        {transitions.map(({ item, key, props }) =>
          item ? (
            <animated.div style={props} key={key}>
              {renderInfo(
                iconType || "checkmark",
                swipeConfirmKey || "swipe.home.cancel"
              )}
            </animated.div>
          ) : (
            <animated.div style={props} key={key}>
              {renderInfo("chevron-up", swipeMessageKey || "swipe.home.info")}
            </animated.div>
          )
        )}
      </div>
    </div>
  );
};

interface BottomPageDivProps {
  style: React.CSSProperties;
  swipeMessageKey?: string;
  swipeConfirmKey?: string;
  iconType?: IconType;
}

export default withRouter(SwipeUp);
